// src/components/chat/DragDropOverlay.tsx

import * as React from "react";

/**
 * Props for the DragDropOverlay component
 */
interface DragDropOverlayProps {
  isVisible: boolean;
  message?: string;
  subMessage?: string;
  onClose?: () => void;
}

/**
 * DragDropOverlay Component
 *
 * Displays a full-screen overlay when files are being dragged over the application.
 * Features:
 * - Semi-transparent backdrop
 * - Centered drop zone indicator with dashed border
 * - Animated entrance/exit
 * - Customizable messaging
 */
const DragDropOverlay: React.FC<DragDropOverlayProps> = ({
  isVisible,
  message = "Drop your files here",
  subMessage = "PDF, DOCX, Excel files supported",
  onClose,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  if (!isVisible) return null;

  return (
    <div style={overlayStyles} className="drag-drop-overlay">
      <div style={dropZoneStyles} className="drop-zone-indicator">
        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={closeButtonStyles(isHovered)}
            aria-label="Close file upload overlay"
          >
            ×
          </button>
        )}

        {/* Icon Container with Background */}
        <div style={iconContainerStyles}>
          {/* Upload Icon SVG */}
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ animation: "gentlePulse 2s ease-in-out infinite" }}
          >
            <path
              d="M7 10L12 5L17 10"
              stroke="#0A8CBB"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 5V15"
              stroke="#0A8CBB"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 13V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V13"
              stroke="#0A8CBB"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h3 style={titleStyles}>{message}</h3>
        {subMessage && <p style={subMessageStyles}>{subMessage}</p>}

        {/* File Type Badges */}
        <div style={fileTypesContainerStyles}>
          <span style={fileTypeBadgeStyles}>DOCX</span>
          <span style={fileTypeBadgeStyles}>Excel</span>
          <span style={fileTypeBadgeStyles}>PDF</span>
        </div>
      </div>
    </div>
  );
};

// Styles for the overlay
const overlayStyles: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.08)",
  backdropFilter: "blur(2px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  pointerEvents: "none", // Allow drag events to pass through
  animation: "fadeIn 0.2s ease-out",
};

// Styles for the drop zone indicator
const dropZoneStyles: React.CSSProperties = {
  position: "relative", // Enable absolute positioning for close button
  padding: "40px 56px",
  border: "2px dashed #0A8CBB",
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",
  backgroundImage: "radial-gradient(circle at 1px 1px, #F5F5F5 1px, transparent 1px)",
  backgroundSize: "20px 20px",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  animation: "scaleIn 0.3s ease-out",
  minWidth: "320px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
};

// Styles for the icon container
const iconContainerStyles: React.CSSProperties = {
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, rgba(10, 140, 187, 0.08) 0%, rgba(10, 140, 187, 0.03) 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "20px",
};

// Styles for the title
const titleStyles: React.CSSProperties = {
  margin: "0 0 8px 0",
  fontSize: "20px",
  fontWeight: 600,
  color: "#333",
  fontFamily: "'Segoe UI', sans-serif",
};

// Styles for the sub-message
const subMessageStyles: React.CSSProperties = {
  margin: "0 0 16px 0",
  fontSize: "14px",
  color: "#757575",
  fontFamily: "'Segoe UI', sans-serif",
};

// Styles for file types container
const fileTypesContainerStyles: React.CSSProperties = {
  display: "flex",
  gap: "8px",
  marginTop: "8px",
};

// Styles for file type badges
const fileTypeBadgeStyles: React.CSSProperties = {
  padding: "4px 12px",
  borderRadius: "16px",
  backgroundColor: "#F5F5F5",
  color: "#666",
  fontSize: "12px",
  fontWeight: 500,
  fontFamily: "'Segoe UI', sans-serif",
  border: "1px solid #E0E0E0",
};

// Styles for close button (matching Document Library style)
const closeButtonStyles = (isHovered: boolean): React.CSSProperties => ({
  position: "absolute",
  top: "12px",
  right: "12px",
  background: isHovered ? "#f3f4f6" : "none",
  border: "none",
  color: isHovered ? "#111827" : "#6b7280",
  fontSize: "28px",
  lineHeight: "1",
  cursor: "pointer",
  padding: "0",
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px",
  transition: "all 0.2s",
  fontFamily: "'Segoe UI', sans-serif",
  pointerEvents: "auto", // Make button clickable even though overlay has pointerEvents: none
});

export default DragDropOverlay;
